import { defineStore } from 'pinia';
import apiService from '@/services/api.service';
import { Config } from '@/config';
import { useAuthStore, useCommunityStore } from '.';
import { Constants } from '@/constants';
import router from '@/router';

import type { Locales, LocalizableString, ProjectDto } from '@/services/types/api.types';
import { FileUploaderFactory } from '@/factories/file-uploader.factory';
import { ProcessorType, type SavingOptions } from '@/services/types/file-uploader.types';

export interface ProjectTemplatesState {
  projectTemplates: ProjectDto[];
  isLoading: boolean;
  itemLoadingStates: Set<number>;
}

function insertOrUpdate<T>(array: T[], item: T, key: keyof T) {
  const index = array.findIndex((i) => i[key] === item[key]);
  if (index !== -1) {
    array[index] = item;
  } else {
    array.push(item);
  }
}

const findProjectTemplate = (projectTemplates: ProjectDto[], projectTemplateId: number) => {
  return projectTemplates.find((x) => {
    return x.id == projectTemplateId;
  });
};

export const useProjectTemplateStore = defineStore({
  id: 'projectTemplates',
  state: (): ProjectTemplatesState => ({
    projectTemplates: [],
    isLoading: false,
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (id: number) => state.itemLoadingStates.has(id),
    getItem:
      (state) =>
      (id: number): ProjectDto | undefined =>
        state.projectTemplates.find((c) => c.id === id),
    previewURL: (state) => (projectTemplateId: number) => {
      const projectTemplate = findProjectTemplate(state.projectTemplates, projectTemplateId);
      const communityStore = useCommunityStore();
      if (projectTemplate) {
        const communityUrl = communityStore.previewURL(projectTemplate.community_id);
        return `${communityUrl}/project-template/${projectTemplate.slug}`;
      }
    },
  },
  actions: {
    async fetchById(id: number) {
      this.itemLoadingStates.add(id);
      try {
        const { data } = await apiService.get<ProjectDto>(`${Config.api.v2}/project-templates/${id}`);
        insertOrUpdate(this.projectTemplates, data, 'id');
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(id);
      }
    },
    async fetchAll() {
      this.isLoading = true;
      try {
        const { data } = await apiService.get<ProjectDto[]>(`${Config.api.v2}/project-templates/`);
        for (const projectTemplate of data) {
          insertOrUpdate(this.projectTemplates, projectTemplate, 'id');
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async delete(projectTemplateId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/project-templates/${projectTemplateId}`);
        const index = this.projectTemplates.findIndex((s) => s.id === projectTemplateId);
        this.projectTemplates.splice(index, 1);
      } catch (error) {
        console.error(error);
      }
    },
    create(options: {
      title?: LocalizableString;
      description?: LocalizableString;
      cloneFromId?: number;
      languageEnabled: Locales[];
    }): ProjectDto {
      const authStore = useAuthStore();
      return {
        community_id: authStore.currentCommunity ?? -1,
        slug: '',
        status: Constants.Project.Status.PreLaunch,
        title: options.title ?? { en: '', fr: '' },
        description: options.description ?? { en: '', fr: '' },
        theme_key: null,
        visibility: Constants.Project.Visibility.Public,
        order_num: -1,
        options: { show_private_everyone: false, automatic_publish: false },
        private_email_allowed: { email: [], domain: [] },
        user_permissions: [],
        is_template: 1,
        open_graph_file: null,
        banner_file: null,
        clone_from_id: options.cloneFromId,
        language_enabled: options.languageEnabled,
      };
    },
    async save(projectTemplate: ProjectDto) {
      try {
        let projectTemplateId = projectTemplate.id;
        if (projectTemplateId) {
          await apiService.put<ProjectDto>(`${Config.api.v2}/project-templates/${projectTemplateId}`, projectTemplate);
        } else {
          const { data } = await apiService.post<ProjectDto>(`${Config.api.v2}/project-templates`, projectTemplate);
          projectTemplateId = data.id;
          router.push({ name: 'project_template_details', params: { id: projectTemplateId } });
        }
        await this.fetchById(projectTemplateId!);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async saveBanner(projectTemplateId: number, options: SavingOptions): Promise<boolean> {
      try {
        await FileUploaderFactory.create(options.version).upload({
          image: options.image,
          method: apiService.post,
          processorType: ProcessorType.Multer,
          url: `${Config.api.v2}/project-templates/${projectTemplateId}/banner`,
        });
        await this.fetchById(projectTemplateId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async removeBanner(projectTemplateId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/project-templates/${projectTemplateId}/banner`);
        await this.fetchById(projectTemplateId);
      } catch (error) {
        console.error(error);
      }
    },
    async saveOpenGraph(projectTemplateId: number, options: SavingOptions): Promise<boolean> {
      try {
        await FileUploaderFactory.create(options.version).upload({
          image: options.image,
          method: apiService.post,
          processorType: ProcessorType.Multer,
          url: `${Config.api.v2}/project-templates/${projectTemplateId}/open-graph`,
        });
        await this.fetchById(projectTemplateId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async removeOpenGraph(projectTemplateId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/project-templates/${projectTemplateId}/open-graph`);
        await this.fetchById(projectTemplateId);
      } catch (error) {
        console.error(error);
      }
    },
  },
});
